import type { FC } from "react";

import { MetaLine } from "@/components/ui/meta-line";

import { useOpenEntity } from "../hooks/useOpenEntity";
import { onScreenIcon } from "../onScreen/onScreenIcon";
import {
  kindLabel,
  parseEntityHref,
  type EntityRefTarget
} from "../refs/entityRefs";
import type { TurnContextRef } from "../useAgentChat";

const targetOf = (context: TurnContextRef): EntityRefTarget | null => {
  if (!context.route) return null;
  const parsed = parseEntityHref(context.route);
  if (parsed && parsed.kind === context.kind && parsed.id === context.id)
    return parsed;
  return { kind: context.kind, path: context.route, id: context.id };
};

/**
 * Under a user message: the record that was open on the page when it was
 * sent, as the server checked it. Clicking it opens the record again.
 */
export const TurnContextLine: FC<{ context: TurnContextRef }> = ({
  context
}) => {
  const { open } = useOpenEntity();
  const Icon = onScreenIcon(context.kind);
  const kind = kindLabel({ kind: context.kind });
  const label = context.label || kind;
  const target = targetOf(context);
  return (
    <MetaLine
      data-testid="turn-context"
      className="px-1"
      title={kind}
      parts={[
        <span key="context" className="inline-flex min-w-0 items-center gap-1">
          <Icon className="size-3 shrink-0" />
          {target ? (
            <button
              type="button"
              className="min-w-0 truncate hover:text-fg"
              onClick={() => open({ target, label, title: null })}
            >
              {label}
            </button>
          ) : (
            <span className="min-w-0 truncate">{label}</span>
          )}
        </span>
      ]}
    />
  );
};
